import React, { useState, useEffect, useCallback } from 'react';
import { superadminService } from '../../services/authService';
import { format } from 'date-fns';

const ProjectsAdminPage = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await superadminService.getAllProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Superadmin role required.');
      } else {
        setError('Failed to load projects');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleToggleSimulation = async (projectId, currentStatus) => {
    setActionLoading(projectId);
    setError(null);
    try {
      const result = await superadminService.toggleProjectSimulation(projectId, !currentStatus);
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update project');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveRequest = async (projectId) => {
    setActionLoading(projectId);
    setError(null);
    try {
      const result = await superadminService.approveSimulationRequest(projectId);
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectRequest = async (e) => {
    e.preventDefault();
    if (!rejectModal) return;
    setActionLoading(rejectModal);
    setError(null);
    try {
      const result = await superadminService.rejectSimulationRequest(rejectModal, rejectReason);
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setRejectModal(null);
      setRejectReason('');
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject request');
    } finally {
      setActionLoading(null);
    }
  };

  const pendingRequests = projects.filter(p => p.simulation_request_status === 'pending');
  const otherProjects = projects.filter(p => p.simulation_request_status !== 'pending');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Project Management</h1>
          <p className="text-dark-400 mt-1">
            Enable or disable simulation capabilities for projects
          </p>
        </div>
        <button
          onClick={fetchProjects}
          className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/20 text-error-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-success-500/10 border border-success-500/20 text-success-400 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Pending Requests Section */}
      {pendingRequests.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-yellow-500/20">
            <h2 className="text-lg font-semibold text-yellow-400 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pending Simulation Requests ({pendingRequests.length})
            </h2>
          </div>
          <div className="divide-y divide-yellow-500/10">
            {pendingRequests.map((project) => (
              <div key={project.id} className="p-6 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-dark-100">{project.name}</h3>
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                      Pending
                    </span>
                  </div>
                  <p className="text-dark-400 text-sm mt-1">
                    Owner: {project.owner_email || 'Unknown'} &middot; {project.members_count} members
                  </p>
                  {project.simulation_request_reason && (
                    <div className="mt-3 p-3 bg-dark-800 rounded-lg">
                      <p className="text-dark-500 text-xs uppercase mb-1">Reason</p>
                      <p className="text-dark-300 text-sm">{project.simulation_request_reason}</p>
                    </div>
                  )}
                  {project.simulation_request_at && (
                    <p className="text-dark-500 text-xs mt-2">
                      Requested {format(new Date(project.simulation_request_at), 'MMM d, yyyy HH:mm')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 ml-6">
                  <button
                    onClick={() => handleApproveRequest(project.id)}
                    disabled={actionLoading === project.id}
                    className="px-4 py-2 bg-success-500/20 text-success-400 hover:bg-success-500/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === project.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => setRejectModal(project.id)}
                    disabled={actionLoading === project.id}
                    className="px-4 py-2 bg-error-500/20 text-error-400 hover:bg-error-500/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Project
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Members
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Simulations
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {otherProjects.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-dark-400">
                    No projects found
                  </td>
                </tr>
              ) : (
                otherProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-dark-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-dark-100">{project.name}</div>
                      <div className="text-xs text-dark-500 mt-0.5">{project.id}</div>
                    </td>
                    <td className="px-6 py-4 text-dark-300">
                      {project.owner_email || '-'}
                    </td>
                    <td className="px-6 py-4 text-dark-300">
                      {project.members_count}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        project.status === 'active'
                          ? 'bg-success-500/20 text-success-400'
                          : project.status === 'suspended'
                          ? 'bg-error-500/20 text-error-400'
                          : 'bg-warning-500/20 text-warning-400'
                      }`}>
                        {project.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        project.simulation_enabled
                          ? 'bg-primary-500/20 text-primary-400'
                          : 'bg-dark-600 text-dark-400'
                      }`}>
                        {project.simulation_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {project.simulation_enabled && project.simulation_enabled_at && (
                        <div className="text-xs text-dark-500 mt-1">
                          by {project.simulation_enabled_by}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-dark-400 text-sm">
                      {project.created_at
                        ? format(new Date(project.created_at), 'MMM d, yyyy')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleToggleSimulation(project.id, project.simulation_enabled)}
                        disabled={actionLoading === project.id}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          project.simulation_enabled
                            ? 'bg-error-500/20 text-error-400 hover:bg-error-500/30'
                            : 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                        } disabled:opacity-50`}
                      >
                        {actionLoading === project.id ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current"></div>
                            Processing...
                          </span>
                        ) : project.simulation_enabled ? (
                          'Disable Simulations'
                        ) : (
                          'Enable Simulations'
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-dark-800/50 border border-dark-700 rounded-xl p-6">
        <h3 className="text-lg font-medium text-dark-200 mb-3">About Simulation Permissions</h3>
        <div className="text-dark-400 text-sm space-y-2">
          <p>
            <strong className="text-dark-200">Project Simulation Capability:</strong> Controls whether a project
            can run simulations at all. Only superadmins can toggle this.
          </p>
          <p>
            <strong className="text-dark-200">Member Launch Permission:</strong> Within an enabled project,
            the project owner controls which members can launch simulations.
          </p>
          <p>
            <strong className="text-dark-200">Owners:</strong> Project owners always have permission to launch
            simulations (if the project has simulation capability enabled).
          </p>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-dark-100 mb-4">Reject Request</h2>
            <p className="text-dark-400 text-sm mb-4">
              Provide a reason for rejecting this simulation access request.
            </p>
            <form onSubmit={handleRejectRequest}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Rejection Reason (optional)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why the request is being rejected..."
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent h-24 resize-none"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRejectModal(null);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === rejectModal}
                  className="px-4 py-2 bg-error-500/20 text-error-400 hover:bg-error-500/30 rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === rejectModal ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsAdminPage;
